import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { getStatusBadgeStyle, formatDateShort } from '../lib/utils';
import { Loader2, ChevronRight, Building2, CalendarClock, User as UserIcon } from 'lucide-react';
import type { ApiResponse, Opportunity } from '../types';

export function Dashboard() {
    const api = useApi();
    const { user } = useAuth();

    const { data, isLoading } = useQuery({
        queryKey: ['opportunities'],
        queryFn: () => api.get<ApiResponse<Opportunity[]>>('/api/opportunities'),
    });

    const opportunities = data?.data || [];

    // Le dashboard n'affiche que les avant-ventes où l'utilisateur est owner
    // ou consultant — pas l'ensemble des avant-ventes de l'outil.
    const mine = opportunities
        .filter((o) => o.ownerId === user?.id || o.consultants.some((c) => c.id === user?.id))
        .map((o) => ({
            ...o,
            myRoles: [
                o.ownerId === user?.id && 'Owner',
                o.consultants.some((c) => c.id === user?.id) && 'Consultant',
            ].filter(Boolean) as string[],
        }));

    const active = mine.filter((o) => o.statusOutcome === 'open');
    const won = mine.filter((o) => o.statusOutcome === 'won');
    const lost = mine.filter((o) => o.statusOutcome === 'lost');
    const noDecision = mine.filter((o) => o.statusOutcome === 'no_decision');

    const byStatus = active.reduce<Record<string, number>>((acc, o) => {
        acc[o.statusValue] = (acc[o.statusValue] || 0) + 1;
        return acc;
    }, {});

    const upcoming = active
        .filter((o) => o.restitutionDate)
        .sort((a, b) => new Date(a.restitutionDate!).getTime() - new Date(b.restitutionDate!).getTime())
        .slice(0, 5);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-[var(--color-text)]">Dashboard</h1>
                <p className="text-[var(--color-text-secondary)]">
                    {active.length} avant-vente{active.length > 1 ? 's' : ''} en cours vous concernant
                </p>
            </div>

            <div className="card">
                <div className="flex items-center gap-2 mb-4">
                    <UserIcon className="w-4 h-4 text-[var(--color-text-muted)]" />
                    <h2 className="text-sm font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide">
                        Mes avant-ventes
                    </h2>
                </div>
                {active.length === 0 ? (
                    <p className="text-sm text-[var(--color-text-muted)]">
                        Aucune avant-vente ouverte où vous êtes owner ou consultant pour l'instant
                    </p>
                ) : (
                    <div className="space-y-2">
                        {active.map((o) => (
                            <Link
                                key={o.id}
                                to="/opportunities"
                                className="flex items-center justify-between p-3 rounded-lg hover:bg-[var(--color-bg-hover)] transition-colors"
                                style={{ borderLeft: `4px solid ${o.statusColor || 'transparent'}` }}
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <Building2 className="w-4 h-4 text-[var(--color-text-muted)] flex-shrink-0" />
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-[var(--color-text)] truncate">{o.name}</p>
                                        <p className="text-xs text-[var(--color-text-secondary)]">{o.client}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    {o.myRoles.map((role) => (
                                        <span key={role} className="badge badge-default">{role}</span>
                                    ))}
                                    {o.practices.map((p) => (
                                        <span key={p.id} className="badge badge-default">{p.value}</span>
                                    ))}
                                    <span className="badge" style={getStatusBadgeStyle(o.statusValue, o.statusOutcome, o.statusColor)}>
                                        {o.statusValue}
                                    </span>
                                    <ChevronRight className="w-4 h-4 text-[var(--color-text-muted)]" />
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>

            {Object.keys(byStatus).length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {Object.entries(byStatus).map(([status, count]) => (
                        <div key={status} className="card">
                            <p className="text-2xl font-bold text-[var(--color-text)]">{count}</p>
                            <p className="text-sm text-[var(--color-text-secondary)]">{status}</p>
                        </div>
                    ))}
                </div>
            )}

            <div className="grid grid-cols-3 gap-4">
                <div className="card text-center">
                    <p className="text-xl font-bold text-[var(--color-success)]">{won.length}</p>
                    <p className="text-xs text-[var(--color-text-secondary)]">Gagnées</p>
                </div>
                <div className="card text-center">
                    <p className="text-xl font-bold text-[var(--color-error)]">{lost.length}</p>
                    <p className="text-xs text-[var(--color-text-secondary)]">Perdues</p>
                </div>
                <div className="card text-center">
                    <p className="text-xl font-bold text-[var(--color-text-muted)]">{noDecision.length}</p>
                    <p className="text-xs text-[var(--color-text-secondary)]">Sans suite</p>
                </div>
            </div>

            <div className="card">
                <div className="flex items-center gap-2 mb-4">
                    <CalendarClock className="w-4 h-4 text-[var(--color-text-muted)]" />
                    <h2 className="text-sm font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide">
                        Prochaines restitutions
                    </h2>
                </div>
                {upcoming.length === 0 ? (
                    <p className="text-sm text-[var(--color-text-muted)]">Aucune date de restitution à venir</p>
                ) : (
                    <div className="space-y-2">
                        {upcoming.map((o) => (
                            <Link
                                key={o.id}
                                to="/opportunities"
                                className="flex items-center justify-between p-3 rounded-lg hover:bg-[var(--color-bg-hover)] transition-colors"
                                style={{ borderLeft: `4px solid ${o.statusColor || 'transparent'}` }}
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <Building2 className="w-4 h-4 text-[var(--color-text-muted)] flex-shrink-0" />
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-[var(--color-text)] truncate">{o.name}</p>
                                        <p className="text-xs text-[var(--color-text-secondary)]">{o.client}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 flex-shrink-0">
                                    {o.practices.map((p) => (
                                        <span key={p.id} className="badge badge-default">{p.value}</span>
                                    ))}
                                    <span className="badge" style={getStatusBadgeStyle(o.statusValue, o.statusOutcome, o.statusColor)}>
                                        {o.statusValue}
                                    </span>
                                    <span className="text-sm text-[var(--color-text-secondary)]">
                                        {formatDateShort(o.restitutionDate)}
                                    </span>
                                    <ChevronRight className="w-4 h-4 text-[var(--color-text-muted)]" />
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
